import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
  CardHeader,
} from "@/components/ui/card";
import { Users, UserCheck, Award, Clock, BarChart3 } from "lucide-react";
import { memo } from "react";
import { formatCountWithArabicNumbers } from "@/utils/arabicNumbers";

interface DashboardStatsProps {
  totalStudents: number;
  presentStudents: number;
  absentStudents: number;
  lateStudents: number;
  withReason?: number;
}

export const DashboardStats = memo(
  ({
    totalStudents,
    presentStudents,
    absentStudents,
    lateStudents,
    withReason,
  }: DashboardStatsProps) => {
    return (
      <Card className="bg-gradient-to-br from-white/95 to-white/80 backdrop-blur-lg shadow-xl border-0 rounded-2xl lg:rounded-3xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-600/10"></div>
        <CardHeader className="relative z-10 pb-3 p-3 lg:p-4 bg-secondary text-white rounded-t-2xl lg:rounded-t-3xl">
          <CardTitle className="text-base lg:text-lg font-bold text-white text-left flex items-center justify-start gap-2">
            <BarChart3 className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
            <span>البيانات اليومية</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 lg:p-4 pt-4 relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
            {/* إجمالي الطلاب */}
            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 border-l-4 border-l-brand-blue">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-brand-blue text-white flex items-center justify-center shadow-lg flex-shrink-0">
                    <Users className="w-6 h-6" />
                  </div>
                  <CardDescription className="text-sm text-foreground/70 font-medium">
                    العدد الكلي
                  </CardDescription>
                </div>
                <CardTitle className="text-2xl font-bold text-foreground">
                  {formatCountWithArabicNumbers(totalStudents)}
                </CardTitle>
              </div>
            </div>

            {/* الطلاب الحاضرون */}
            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 border-l-4 border-l-brand-green">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-brand-green text-white flex items-center justify-center shadow-lg flex-shrink-0">
                    <UserCheck className="w-6 h-6" />
                  </div>
                  <CardDescription className="text-sm text-foreground/70 font-medium">
                    الحاضرون
                  </CardDescription>
                </div>
                <CardTitle className="text-2xl font-bold text-foreground">
                  {formatCountWithArabicNumbers(presentStudents)}
                </CardTitle>
              </div>
            </div>

            {/* الطلاب المتأخرون */}
            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 border-l-4 border-l-warning">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-warning text-white flex items-center justify-center shadow-lg flex-shrink-0">
                    <Clock className="w-6 h-6" />
                  </div>
                  <CardDescription className="text-sm text-foreground/70 font-medium">
                    المتأخرون
                  </CardDescription>
                </div>
                <CardTitle className="text-2xl font-bold text-foreground">
                  {formatCountWithArabicNumbers(lateStudents)}
                </CardTitle>
              </div>
            </div>

            {/* الطلاب الغائبون */}
            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 border-l-4 border-l-destructive">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-destructive text-white flex items-center justify-center shadow-lg flex-shrink-0">
                    <Users className="w-6 h-6" />
                  </div>
                  <div className="flex flex-col mt-2">
                    <CardDescription className="text-sm text-foreground/70 font-medium ">
                      الغائبون
                    </CardDescription>
                    <div>
                      <span className="text-xs text-foreground/70 font-medium">
                        الغائبون بعذر :
                      </span>
                      <span className="text-sm text-foreground/70 font-medium">
                        {formatCountWithArabicNumbers(withReason || 0)}
                      </span>{" "}
                    </div>{" "}
                  </div>
                </div>
                <CardTitle className="text-2xl font-bold text-foreground">
                  {formatCountWithArabicNumbers(absentStudents + withReason)}
                </CardTitle>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
);
